import { FIREBASE_EMULATOR_PROJECT_ID } from '@oneapp/shared';
import type { AppCheck } from 'firebase/app-check';
import type { Auth, ConfirmationResult, RecaptchaVerifier, User } from 'firebase/auth';

/**
 * Firebase phone sign-in, loaded only when needed so it stays out of the first download.
 * Locally (VITE_FIREBASE_AUTH_EMULATOR_HOST) it talks to the Auth emulator: no real SMS, no real project.
 */

const env = import.meta.env;
const emulatorHost: string | undefined = env.VITE_FIREBASE_AUTH_EMULATOR_HOST || undefined;
const emulatorProject = env.VITE_FIREBASE_EMULATOR_PROJECT_ID || FIREBASE_EMULATOR_PROJECT_ID;

export type SignInErrorCode =
  | 'wrong-code'
  | 'code-expired'
  | 'too-many-requests'
  | 'quota-exceeded'
  | 'invalid-number'
  | 'blocked'
  | 'not-allowed'
  | 'network'
  | 'unknown';

/** A sign-in problem the screens know how to explain. */
export class SignInError extends Error {
  readonly code: SignInErrorCode;

  constructor(code: SignInErrorCode, cause?: unknown) {
    super(code, { cause });
    this.name = 'SignInError';
    this.code = code;
  }
}

const FIREBASE_CODES: Record<string, SignInErrorCode> = {
  'auth/invalid-verification-code': 'wrong-code',
  'auth/missing-verification-code': 'wrong-code',
  'auth/code-expired': 'code-expired',
  'auth/too-many-requests': 'too-many-requests',
  'auth/quota-exceeded': 'quota-exceeded',
  'auth/invalid-phone-number': 'invalid-number',
  'auth/missing-phone-number': 'invalid-number',
  'auth/user-disabled': 'blocked',
  'auth/operation-not-allowed': 'not-allowed',
  'auth/network-request-failed': 'network',
};

export function toSignInError(error: unknown): SignInError {
  if (error instanceof SignInError) return error;
  const code = (error as { code?: unknown } | null)?.code;
  const known = typeof code === 'string' ? FIREBASE_CODES[code] : undefined;
  // The screen can only say "Something went wrong": what Firebase said is in the console (never the number).
  if (!known) console.warn('Sign-in failed:', typeof code === 'string' ? code : error);
  return new SignInError(known ?? 'unknown', error);
}

export function isSignInConfigured(): boolean {
  return Boolean(emulatorHost || (env.VITE_FIREBASE_API_KEY && env.VITE_FIREBASE_PROJECT_ID));
}

let authPromise: Promise<Auth> | null = null;
/** Firebase App Check, when it's set up (a site key, real Firebase): proves calls come from this app. */
let appCheck: AppCheck | null = null;

/** The Firebase Auth instance, created on first use. Remembers the sign-in across visits. */
export function getFirebaseAuth(): Promise<Auth> {
  authPromise ??= (async () => {
    const [{ initializeApp }, auth] = await Promise.all([import('firebase/app'), import('firebase/auth')]);
    const app = initializeApp(
      emulatorHost
        ? {
            apiKey: 'emulator-api-key',
            authDomain: `${emulatorProject}.firebaseapp.com`,
            projectId: emulatorProject,
          }
        : {
            apiKey: env.VITE_FIREBASE_API_KEY,
            authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
            projectId: env.VITE_FIREBASE_PROJECT_ID,
            appId: env.VITE_FIREBASE_APP_ID,
          },
    );
    // App Check first, so sign-in's own calls (the SMS) carry its token too.
    const siteKey = env.VITE_FIREBASE_APPCHECK_SITE_KEY;
    if (siteKey && !emulatorHost) {
      const { initializeAppCheck, ReCaptchaEnterpriseProvider } = await import('firebase/app-check');
      if (env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN) {
        (self as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
          env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;
      }
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
    }
    const instance = auth.initializeAuth(app, {
      persistence: [auth.indexedDBLocalPersistence, auth.browserLocalPersistence],
    });
    if (emulatorHost) {
      auth.connectAuthEmulator(instance, `http://${emulatorHost}`, { disableWarnings: true });
      // The emulator doesn't check reCAPTCHA; skip it so tests and local sign-in work offline.
      instance.settings.appVerificationDisabledForTesting = true;
    }
    return instance;
  })();
  return authPromise;
}

export async function watchSignIn(callback: (user: User | null) => void): Promise<() => void> {
  const [instance, { onAuthStateChanged }] = await Promise.all([getFirebaseAuth(), import('firebase/auth')]);
  return onAuthStateChanged(instance, callback);
}

let verifier: RecaptchaVerifier | null = null;

/**
 * A new element for the invisible reCAPTCHA, inside the one with `containerId`. Clearing a verifier doesn't take an
 * invisible widget off the page, and reCAPTCHA won't render twice in one element: without a fresh one, a second
 * try (after an error, or "Resend code") fails before any code is sent.
 */
function freshRecaptchaBox(containerId: string): HTMLElement {
  const host = document.getElementById(containerId);
  if (!host) throw new SignInError('unknown');
  host.replaceChildren();
  const box = document.createElement('div');
  host.appendChild(box);
  return box;
}

/**
 * Sends a code by SMS. reCAPTCHA runs invisibly in the element with `containerId` (skipped with the
 * emulator). `language` sets the language of the SMS.
 */
export async function sendSignInCode(
  phoneE164: string,
  containerId: string,
  language: string,
): Promise<ConfirmationResult> {
  const [instance, { RecaptchaVerifier, signInWithPhoneNumber }] = await Promise.all([
    getFirebaseAuth(),
    import('firebase/auth'),
  ]);
  instance.languageCode = language;
  try {
    verifier?.clear();
    verifier = new RecaptchaVerifier(instance, freshRecaptchaBox(containerId), { size: 'invisible' });
    return await signInWithPhoneNumber(instance, phoneE164, verifier);
  } catch (error) {
    verifier?.clear();
    verifier = null;
    throw toSignInError(error);
  }
}

export async function confirmSignInCode(confirmation: ConfirmationResult, code: string): Promise<User> {
  try {
    const credential = await confirmation.confirm(code);
    return credential.user;
  } catch (error) {
    throw toSignInError(error);
  }
}

/**
 * Confirm it's them (an admin's riskiest changes, gap 2): a code by SMS to the mobile they're signed in
 * with. reCAPTCHA runs invisibly in `containerId` (skipped with the emulator).
 */
export async function sendReauthCode(containerId: string, language: string): Promise<ConfirmationResult> {
  const [instance, { RecaptchaVerifier, reauthenticateWithPhoneNumber }] = await Promise.all([
    getFirebaseAuth(),
    import('firebase/auth'),
  ]);
  const user = instance.currentUser;
  if (!user?.phoneNumber) throw new SignInError('not-allowed');
  instance.languageCode = language;
  try {
    verifier?.clear();
    verifier = new RecaptchaVerifier(instance, freshRecaptchaBox(containerId), { size: 'invisible' });
    return await reauthenticateWithPhoneNumber(user, user.phoneNumber, verifier);
  } catch (error) {
    verifier?.clear();
    verifier = null;
    throw toSignInError(error);
  }
}

/** The code from `sendReauthCode`: right, and the sign-in is fresh (a new ID token says so). */
export async function confirmReauthCode(confirmation: ConfirmationResult, code: string): Promise<void> {
  try {
    const credential = await confirmation.confirm(code);
    await credential.user.getIdToken(true);
  } catch (error) {
    throw toSignInError(error);
  }
}

/** The current ID token for API calls; `forceRefresh` gets a new one when the server says it expired. */
export async function currentIdToken(forceRefresh = false): Promise<string | null> {
  const instance = await getFirebaseAuth();
  return instance.currentUser ? instance.currentUser.getIdToken(forceRefresh) : null;
}

export async function signOutOfFirebase(): Promise<void> {
  const [instance, { signOut }] = await Promise.all([getFirebaseAuth(), import('firebase/auth')]);
  await signOut(instance);
}

/** A Firebase App Check token for API calls, or null when App Check isn't set up (or can't answer). */
export async function appCheckToken(): Promise<string | null> {
  await getFirebaseAuth();
  if (!appCheck) return null;
  try {
    const { getToken } = await import('firebase/app-check');
    return (await getToken(appCheck, false)).token;
  } catch {
    // The API says so if it needs one.
    return null;
  }
}
